// NumberSpinner - +/- buttons ([data-number-spinner="increase|decrease"])
// driving a numeric input referenced by [data-controls]. Behavioral port of
// v1 modules/NumberSpinner.js.

export interface NumberSpinnerConfig {
  step: number;
  targetDataAttribute: string;
}

const defaultConfig: NumberSpinnerConfig = {
  step: 1,
  targetDataAttribute: 'data-controls',
};

export default class NumberSpinner {
  selector: string;

  config: NumberSpinnerConfig;

  spinners = new Set<Element>();

  constructor(selector = 'data-number-spinner', config: Partial<NumberSpinnerConfig> = {}) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };
    this._init();
  }

  private _init = (): void => {
    const spinners = document.querySelectorAll(`[${this.selector}]`);
    spinners.forEach(spinner => {
      spinner.addEventListener('click', this._onClick);
      this.spinners.add(spinner);
    });
  };

  private _onClick = (e: Event): void => {
    const button = e.currentTarget as Element;
    const action = button.getAttribute(this.selector);
    const targetSelector = button.getAttribute(this.config.targetDataAttribute);
    const input = targetSelector
      ? document.querySelector<HTMLInputElement>(targetSelector)
      : null;

    if (!input || !action) {
      return;
    }

    const currentValue = Number(input.value) || 0;
    const step = Number(input.getAttribute('step')) || this.config.step;

    const newValue = this._calculateNewValue(action, currentValue, step, input);
    if (newValue !== currentValue) {
      input.value = String(newValue);
      // Trigger change event for any listeners
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  private _calculateNewValue = (
    action: string,
    currentValue: number,
    step: number,
    input: HTMLInputElement,
  ): number => {
    // v1 quirk fixed: Number(null) === 0, so a missing max/min attribute
    // clamped every spinner to 0. Clamp only when the attribute is present.
    if (action === 'increase') {
      const maxAttr = input.getAttribute('max');
      const max = maxAttr === null ? NaN : Number(maxAttr);
      const newValue = currentValue + step;
      return Number.isFinite(max) ? Math.min(newValue, max) : newValue;
    }

    if (action === 'decrease') {
      const minAttr = input.getAttribute('min');
      const min = minAttr === null ? NaN : Number(minAttr);
      const newValue = currentValue - step;
      return Number.isFinite(min) ? Math.max(newValue, min) : newValue;
    }

    return currentValue;
  };

  destroy = (): void => {
    this.spinners.forEach(spinner => {
      spinner.removeEventListener('click', this._onClick);
    });
    this.spinners.clear();
  };

  update = (): void => {
    this.destroy();
    this._init();
  };
}
